import React, { useState, useRef, useCallback, useEffect } from 'react';
import { toast } from 'react-toastify';
import { Flex, HStack, useBoolean } from '@chakra-ui/react';

import api from '~/services/api';
import { getParam } from '~/utils/url';

import TaxDeductionsModal from './TaxDeductionsModal';

import { Modal } from '~/components/UI/Modal';
import { ModalContainer } from '~/components/UI/Modal';
import { ISpec, IText, IMoney } from '~/components/UI/Inputs';
import { Button, Alert, Divider } from '~/components/ChakraComponents';

export default function EditContractModal({ group, contract, closeModal }) {
  const { hospitalId } = getParam('hospitalId');
  const [loading, setLoading] = useBoolean();

  const [inputs, setInputs] = useState({
    specialty: contract.specialty.id,
    specialtyName: contract.specialty.name,
    sector: contract.sector,
    start: contract.start.slice(0, -3),
    end: contract.end.slice(0, -3),
    value: contract.value + '',
    payer: contract.payer,
    closeVacancyDay: contract.closeVacancyDay,
    closeFinanceDay: contract.closeFinanceDay,
  });
  const [taxDeductions, setTaxDeductions] = useState(contract.taxDeductions);
  const [confirmSubmit, setConfirmSubmit] = useBoolean();

  const taxDeductionsModalRef = useRef(null);

  const openTaxDeductions = useCallback(() => {
    taxDeductionsModalRef.current.openModal();
  }, []);

  const handleChanges = e => {
    setInputs({ ...inputs, [e.target.name]: e.target.value });
  };

  const handleChangesSpec = specialty => {
    setInputs({ ...inputs, specialty });
  };

  const onSubmit = () => {
    if (!inputs.sector || !inputs.sector === '') {
      setLoading.off();
      return toast.error('O setor é obrigatorio');
    }

    if (!inputs.start || !inputs.start === '') {
      setLoading.off();
      return toast.error('A hora inicial é obrigatoria');
    }

    if (!inputs.end || !inputs.end === '') {
      setLoading.off();
      return toast.error('A hora final é obrigatoria');
    }

    if (inputs.start.length < 5) {
      setLoading.off();
      return toast.error('Hora inicial inválida.');
    }

    if (inputs.end.length < 5) {
      setLoading.off();
      return toast.error('Hora final inválida.');
    }

    if (inputs.start === inputs.end) {
      setLoading.off();
      return toast.error('Hora inicial e final não podem ser iguais.');
    }

    if (!inputs.value || !inputs.value === '') {
      setLoading.off();
      return toast.error('O valor é obrigatorio');
    }

    if (!inputs.closeVacancyDay || !inputs.closeVacancyDay === '') {
      setLoading.off();
      return toast.error('O dia de fechamento é obrigatorio');
    }

    if (!inputs.closeFinanceDay || !inputs.closeFinanceDay === '') {
      setLoading.off();
      return toast.error('O dia de pagamento é obrigatorio');
    }

    const request = { ...inputs };

    request.group = group.id;
    request.value = +request.value;

    const contractUrl = `/companies/myhospitals/${hospitalId}/contracts/${contract.id}`;

    api.patch(contractUrl, request).then(response => {
      const taxUrl = `/companies/myhospitals/${hospitalId}/contracts/${response.data.id}/taxdeductions`;

      api.patch(taxUrl, { taxDeductions }).then(() => {
        toast.success('Plantão editado com sucesso!');
        setTimeout(() => window.location.reload(), 1200);
      });
    });
  };

  useEffect(() => {
    if (loading) {
      setConfirmSubmit.off();
      onSubmit();
    }
  }, [loading]);

  return (
    <ModalContainer loading={loading}>
      <Flex pb={4} justify="center">
        <Button title="Deduções Fiscais" onClick={openTaxDeductions} />
      </Flex>
      <HStack>
        <ISpec
          required
          specialtyName={inputs.specialtyName}
          setSpecialtyId={handleChangesSpec}
        />
        <IText
          required
          label="setor"
          placeholder="Ex: Emergência"
          name="sector"
          value={inputs.sector}
          onChange={handleChanges}
        />
      </HStack>
      <HStack>
        <IText
          required
          label="horário inicial"
          placeholder="Ex: 07:00"
          name="start"
          value={inputs.start}
          useCleave
          options={{
            time: true,
            timePattern: ['h', 'm'],
          }}
          onChange={handleChanges}
        />
        <IText
          required
          label="horário final"
          placeholder="Ex: 19:00"
          name="end"
          value={inputs.end}
          useCleave
          options={{
            time: true,
            timePattern: ['h', 'm'],
          }}
          onChange={handleChanges}
        />
      </HStack>
      <HStack>
        <IMoney
          required
          label="valor por plantão"
          placeholder="Ex: 1200"
          name="value"
          value={inputs.value}
          onChange={handleChanges}
        />
        <IText
          label="pagador"
          placeholder="Ex: Organização LTDA"
          name="payer"
          value={inputs.payer}
          onChange={handleChanges}
        />
      </HStack>
      <HStack>
        <IText
          required
          label="dia de fechamento"
          placeholder="Ex: 15"
          name="closeVacancyDay"
          value={inputs.closeVacancyDay}
          maxLength={2}
          onChange={handleChanges}
        />
        <IText
          required
          label="dia de pagamento"
          placeholder="Ex: 17"
          name="closeFinanceDay"
          value={inputs.closeFinanceDay}
          maxLength={2}
          onChange={handleChanges}
        />
      </HStack>
      <Divider />
      <Flex justify="space-between">
        <Button title="cancelar" variant="secondary" onClick={closeModal} />
        <Button
          title="editar"
          variant="success"
          onClick={setConfirmSubmit.on}
        />
      </Flex>
      <Alert
        isOpen={confirmSubmit}
        onClose={setConfirmSubmit.off}
        onConfirm={setLoading.on}
      />
      <Modal
        title="Deduções Fiscais"
        ref={taxDeductionsModalRef}
        component={TaxDeductionsModal}
        taxDeductions={taxDeductions}
        setTaxDeductions={setTaxDeductions}
      />
    </ModalContainer>
  );
}
